import React, { useState, useEffect, use } from "react";
import {
  Grid,
  TextField,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Tooltip,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Autocomplete,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation } from "react-router";
import { Visibility } from "@mui/icons-material";
import {
  ApproveSolistapi,
  GeneralVoucherCreate,
  GetNextGeneralNo,
  GeneralVoucherUpdate,
  ApproveExpenseList,
  GetApprovedSuppliers,
  ApproveImprestList,
  ApproveAdvworkerList,
  ApproveTransporterList,
} from "../../../Services/Services";

const GeneralVoucherForm = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = location.state?.editMode || false;
  const editData = location.state?.voucherData || null;

  const getSelectedDivision = () => {
    try {
      const stored = localStorage.getItem("selectedDivision");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error("Failed to parse selectedDivision:", e);
      return null;
    }
  };

  const storedDivision = getSelectedDivision();

  const [projectLists, setProjectLists] = useState([]);
  const [projectPage, setProjectPage] = useState(1);
  const [projectLoading, setProjectLoading] = useState(false);
  const [projectSearch, setProjectSearch] = useState("");
  const [expenseLists, setExpenseLists] = useState([]);
  const [imprestLists, setImprestLists] = useState([]);
  const [supplierLists, setSupplierLists] = useState([]);
  const [supplierPage, setSupplierPage] = useState(1);
  const [supplierLoading, setSupplierLoading] = useState(false);
  const [supplierSearch, setSupplierSearch] = useState("");
  const [transportersLists, setTransportersLists] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [rows, setRows] = useState([createEmptyRow()]);
  const [loading, setLoading] = useState(false);
  const [Advworkers, setAdvworkers] = useState([]);
  const [existingFileName, setExistingFileName] = useState("");

  // ✅ ONLY LOGIC: Added balance and receipt fields (no UI change)
  const [formData, setFormData] = useState({
    gen_number: "",
    imprest: "",
    supplier: "",
    advance_worker: "",
    project: "",
    division: storedDivision?.division_id || "",
    division_name: storedDivision?.division || "",
    payment_status: "",
    attachment: null,
    narration: "",
    bill_no: "",
    transporter: "",
    bill_date: "",
    receipt_amount: 0, // ✅ ADDED
    balance_amount: 0, // ✅ ADDED
  });

  const costTypeOptions = [
    { value: "processing cost", label: "Processing Cost" },
    { value: "admin cost", label: "Admin Cost" },
    { value: "finance cost", label: "Finance Cost" },
    { value: "machinery cost", label: "Machinery Cost" },
  ];

  // Create empty row for table
  function createEmptyRow() {
    return {
      expense: "",
      cost_type: "",
      bill_no: "",
      bill_date: "",
      tds: 0,
      gst: 0,
      amount: "",
      total_amount: "",
    };
  }
  const fetchSuppliers = async (page = 1, filters = {}) => {
    try {
      setSupplierLoading(true);
      const response = await GetApprovedSuppliers(page, filters);

      // Response could have .results and .next (like DRF paginated)
      const results = Array.isArray(response.results)
        ? response.results
        : response || [];

      // Merge pages
      if (page === 1) {
        setSupplierLists(results);
      } else {
        setSupplierLists((prev) => [...prev, ...results]);
      }

      // Pagination control (Django REST style)
      setSupplierPage(page);
    } catch (err) {
      toast.error("Failed to load project list");
    } finally {
      setSupplierLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      // Search filter trigger
      fetchSuppliers(1, { name: supplierSearch });
    }, 500); // Wait 0.5s after typing stops

    return () => clearTimeout(delayDebounce);
  }, [supplierSearch]);

  // Fetch initial data
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [expenses, imprest, divisions, ad_worker, transporter] =
          await Promise.all([
            ApproveExpenseList(),
            ApproveImprestList(),
            ApproveAdvworkerList(),
            ApproveTransporterList(),
          ]);

        setExpenseLists(expenses || []);
        setImprestLists(imprest || []);
        setAdvworkers(ad_worker || []);
        const transporterData = Array.isArray(transporter)
          ? transporter
          : transporter?.results || transporter?.data || [];
        setTransportersLists(transporterData);
      } catch (err) {
        toast.error("Failed to load dropdown data");
      }
    };

    fetchDropdownData();
  }, []);

  const fetchProjects = async (page = 1, filters = {}) => {
    try {
      setProjectLoading(true);
      const finalFilters = storedDivision?.division_id
        ? { ...filters, division: storedDivision.division_id }
        : filters;
      const response = await ApproveSolistapi(page, finalFilters);

      // Response could have .results and .next (like DRF paginated)
      const results = Array.isArray(response.results)
        ? response.results
        : response || [];

      // Merge pages
      if (page === 1) {
        setProjectLists(results);
      } else {
        setProjectLists((prev) => [...prev, ...results]);
      }

      // Pagination control (Django REST style)
      setProjectPage(page);
    } catch (err) {
      toast.error("Failed to load project list");
    } finally {
      setProjectLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      // Search filter trigger
      fetchProjects(1, { project_name: projectSearch });
    }, 500); // Wait 0.5s after typing stops

    return () => clearTimeout(delayDebounce);
  }, [projectSearch]);

  useEffect(() => {
    const generateVoucherNo = async () => {
      if (!isEditMode) {
        try {
          const nextNo = await GetNextGeneralNo();
          if (nextNo?.gen_voucher_no) {
            setFormData((prev) => ({
              ...prev,
              gen_number: nextNo.gen_voucher_no,
            }));
          }
        } catch (err) {
          toast.error("Failed to generate voucher number");
        }
      }
    };

    generateVoucherNo();
  }, [isEditMode]);

  // Form field change
  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: "" }));
  };

  // ✅ Auto-calculate Total Amount when TDS or Amount changes
  const handleRowChange = (index, key, value) => {
    const updated = [...rows];
    updated[index][key] = value;

    // ✅ Calculate Total Amount = TDS + Amount
    if (key === "tds" || key === "amount" || key === "gst") {
      const tds = parseFloat(updated[index].tds) || 0;
      const gst = parseFloat(updated[index].gst) || 0;
      const amount = parseFloat(updated[index].amount) || 0;
      updated[index].total_amount = (amount + gst - tds).toFixed(2);
    }

    setRows(updated);
  };

  const handleAddRow = () => setRows((prev) => [...prev, createEmptyRow()]);
  const handleDeleteRow = (index) =>
    setRows((prev) => prev.filter((_, i) => i !== index));

  const grandTotal = rows.reduce(
    (sum, row) => sum + (parseFloat(row.total_amount) || 0),
    0,
  );

  // ✅ ONLY LOGIC: Auto-calculate balance and payment status
  useEffect(() => {
    const total = parseFloat(grandTotal) || 0;
    const paid = parseFloat(formData.receipt_amount) || 0;
    const balance = total - paid;

    let paymentStatus = "Unpaid";
    if (balance <= 0 && total > 0) {
      paymentStatus = "Paid";
    } else if (balance > 0 && balance < total) {
      paymentStatus = "Partial";
    }

    setFormData((prev) => ({
      ...prev,
      balance_amount: balance.toFixed(2),
      payment_status: paymentStatus,
    }));
  }, [grandTotal, formData.receipt_amount]);

  const validateForm = () => {
    const errors = {};
    if (!formData.gen_number) errors.gen_number = "Voucher No required";
    if (!formData.narration) errors.narration = "Narration required";
    const validExpenseExists = rows.some(
      (row) => row.expense && row.amount && parseFloat(row.amount) > 0,
    );
    if (!validExpenseExists) {
      errors.expense = "At least one expense is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  function createEmptyRow() {
    return {
      expense: "",
      cost_type: "",
      bill_no: "",
      bill_date: "",
      amount: "",
    };
  }

  // Reset form
  const resetForm = async () => {
    setFormData({
      gen_number: "",
      imprest: "",
      supplier: "",
      advance_worker: "",
      transporter: "",
      project: "",
      division: storedDivision?.division_id || "",
      division_name: storedDivision?.division || "",
      payment_status: "",
      attachment: null,
      narration: "",
      receipt_amount: 0, // ✅ ADDED
      balance_amount: 0, // ✅ ADDED
    });
    setRows([createEmptyRow()]);
    setFormErrors({});

    try {
      const nextNo = await GetNextGeneralNo();
      if (nextNo?.gen_voucher_no) {
        setFormData((prev) => ({
          ...prev,
          gen_number: nextNo.gen_voucher_no,
        }));
      }
    } catch (err) {}
  };

  useEffect(() => {
    if (isEditMode && editData) {
      // ✅ Fill main form data
      setFormData({
        gen_number: editData.gen_number || "",
        imprest: editData.imprest || "",
        supplier: editData.supplier || "",
        advance_worker: editData.advance_worker || "",
        project: editData.project || "",
        division: storedDivision.division || editData.division_id || "",
        division_name:
          storedDivision.division || editData.division_id || "",
        payment_status: editData.payment_status || "",
        attachment: editData.attachment || null,
        narration: editData.narration || "",
        bill_no: editData.bill_no || "",
        bill_date: editData.bill_date || "",
        transporter: editData.transporter || "",
        transporter_name: editData.transporter_name || "",
        receipt_amount: editData.receipt_amount || 0, // ✅ ADDED
        balance_amount: editData.balance_amount || editData.grandtotal || 0, // ✅ ADDED
      });

      if (editData.attachment) {
        const fullPath = editData.attachment.split("/").pop();
        const fileName = fullPath.split("?")[0];

        // ✅ 20 characters se zyada ho to truncate karo
        if (fileName.length > 20) {
          const ext = fileName.split(".").pop();
          const name = fileName.substring(0, 15);
          setExistingFileName(`${name}...${ext}`);
        } else {
          setExistingFileName(fileName);
        }
      }

      // ✅ Fill expense rows
      if (editData.sub_vouchers && editData.sub_vouchers.length > 0) {
        const expenseRows = editData.sub_vouchers.map((item) => ({
          expense: item.expense || "",
          cost_type: item.cost_type || "",
          bill_no: item.bill_no || "",
          bill_date: item.bill_date || "",
          tds: item.tds || 0,
          gst: item.gst || 0,
          amount: item.amount || "",
          total_amount: item.total_amount || "",
        }));
        setRows(expenseRows);
      } else if (editData.expense_rows && editData.expense_rows.length > 0) {
        setRows(editData.expense_rows);
      } else {
        // ✅ If no sub vouchers, create single row with main data
        setRows([
          {
            expense: editData.expense || "",
            cost_type: editData.cost_type || "",
            bill_no: editData.bill_no || "",
            bill_date: editData.bill_date || "",
            amount: editData.grandtotal || "",
          },
        ]);
      }
    }
  }, [isEditMode, editData]);

  // Submit form
  const handleSubmit = async (e) => {
    if (!storedDivision?.division_id){
      toast.error("No division selected. Please select a division from the header.");
    }
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    try {
      const fd = new FormData();
      fd.append("gen_number", formData.gen_number);
      fd.append("imprest", formData.imprest);
      fd.append("supplier", formData.supplier);
      fd.append("advance_worker", formData.advance_worker);
      fd.append("project", formData.project);
      fd.append("division", storedDivision.division_id);
      fd.append("payment_status", formData.payment_status);
      fd.append("narration", formData.narration);
      fd.append("grandtotal", grandTotal);
      fd.append("bill_no", formData.bill_no);
      fd.append("transporter", formData.transporter);
      fd.append("bill_date", formData.bill_date);
      fd.append("receipt_amount", formData.receipt_amount || 0); // ✅ ADDED
      fd.append("balance_amount", formData.balance_amount || grandTotal); // ✅ ADDED

      if (formData.attachment instanceof File) {
        fd.append("attachment", formData.attachment);
      }

      const cleanedRows = rows.map((row) => ({
        expense: row.expense,
        cost_type: row.cost_type,
        gst: row.gst || 0,
        tds: row.tds || 0,
        amount: row.amount,
        total_amount: row.total_amount,
      }));
      fd.append("expenseList", JSON.stringify(cleanedRows));

      if (isEditMode) {
        // ✅ Edit Mode
        fd.append("updated_by", localStorage.getItem("user_id"));
        await GeneralVoucherUpdate(editData.gen_voucher_no, fd);
        toast.success("Voucher updated successfully!");
      } else {
        // ✅ Create Mode
        fd.append("created_by", localStorage.getItem("user_id"));
        await GeneralVoucherCreate(fd);
        toast.success("Voucher created successfully!");
        await resetForm();
      }

      navigate("/dashboard/general-voucher");
    } catch (err) {
      toast.error(
        isEditMode ? "Failed to update voucher" : "Failed to create voucher",
      );
    } finally {
      setLoading(false);
    }
  };

  const combinedAccounts = [
    ...supplierLists.map((s) => ({
      id: s.id,
      label: s.name,
      type: "supplier",
    })),
    ...imprestLists.map((i) => ({
      id: i.id,
      label: i.account_name,
      type: "imprest",
    })),
    ...Advworkers.map((a) => ({
      id: a.id,
      label: a.workername,
      type: "advance_worker",
    })),
    ...transportersLists.map((t) => ({
      id: t.id,
      label: t.transporter_name,
      type: "transporter",
    })),
  ];

  return (
    <>
      {/* ✅ Buttons - Mobile Stack / Desktop Horizontal */}
      {isMobile ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            mt: -3,
            pt: 0,
            mb: 2,
            px: 1,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            sx={{
              borderRadius: 0,
              height: 35,
              fontSize: "0.7rem",
              textTransform: "none",
            }}
          >
            ← Back
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              borderRadius: 0,
              height: 35,
              fontSize: "0.7rem",
              textTransform: "none",
            }}
          >
            {loading ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              "Submit"
            )}
          </Button>
        </Box>
      ) : (
        <Grid>
          <Grid
            item
            xs={12}
            md={3}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 3,
              alignItems: "flex-end",
              width: "100%",
              mt: -3,
              pt: 0,
              mb: 2,
              px: 1,
              ml: -1,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 0,
                height: 35,
                minWidth: 120,
                fontSize: "0.7rem",
                px: 2,
                textTransform: "none",
              }}
            >
              ← Back
            </Button>

            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              sx={{
                borderRadius: 0,
                height: 35,
                minWidth: 140,
                fontSize: "0.7rem",
                px: 2,
                textTransform: "none",
              }}
            >
              {loading ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                "Submit"
              )}
            </Button>
          </Grid>
        </Grid>
      )}

      <ToastContainer position="top-right" autoClose={3000} />

      <Box p={2}>
        {/* Voucher & Form Fields */}
        <Box border="1px solid #ccc" borderRadius="8px" p={2} bgcolor="#fff">
          <Grid container spacing={2}>
            {/* Voucher No */}
            <Grid item xs={12} md={3}>
              <TextField
                size="small"
                fullWidth
                required
                label="Voucher No"
                value={formData.gen_number}
                disabled
                error={!!formErrors.gen_number}
                helperText={formErrors.gen_number}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Autocomplete
                size="small"
                options={combinedAccounts}
                getOptionLabel={(opt) => opt.label || ""}
                isOptionEqualToValue={(option, value) =>
                  option.id === value.id && option.type === value.type
                }
                value={(() => {
                  if (formData.supplier) {
                    const s = supplierLists.find(
                      (s) => s.id === formData.supplier,
                    );
                    return s
                      ? { id: s.id, label: s.name, type: "supplier" }
                      : null;
                  } else if (formData.imprest) {
                    const i = imprestLists.find(
                      (i) => i.id === formData.imprest,
                    );
                    return i
                      ? { id: i.id, label: i.account_name, type: "imprest" }
                      : null;
                  } else if (formData.advance_worker) {
                    const a = Advworkers.find(
                      (a) => a.id === formData.advance_worker,
                    );
                    return a
                      ? {
                          id: a.id,
                          label: a.workername,
                          type: "advance_worker",
                        }
                      : null;
                  } else if (formData.transporter) {
                    const t = transportersLists.find(
                      (t) => t.id === formData.transporter,
                    );
                    return t
                      ? {
                          id: t.id,
                          label: t.transporter_name,
                          type: "transporter",
                        }
                      : null;
                  } else {
                    return null;
                  }
                })()}
                onChange={(e, val) => {
                  if (val?.type === "supplier") {
                    handleFormChange("supplier", val.id);
                    handleFormChange("imprest", "");
                    handleFormChange("transporter", "");
                  } else if (val?.type === "imprest") {
                    handleFormChange("imprest", val.id);
                    handleFormChange("supplier", "");
                  } else if (val?.type === "advance_worker") {
                    handleFormChange("advance_worker", val.id);
                    handleFormChange("supplier", "");
                    handleFormChange("imprest", "");
                  } else if (val?.type === "transporter") {
                    handleFormChange("transporter", val.id);
                  } else {
                    handleFormChange("supplier", "");
                    handleFormChange("imprest", "");
                  }
                }}
                onInputChange={(e, newInputValue) => {
                  setSupplierSearch(newInputValue);
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Ledger Name" />
                )}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Autocomplete
                size="small"
                options={projectLists}
                getOptionLabel={(opt) => opt.project_name || ""}
                value={
                  projectLists.find((opt) => opt.id === formData.project) ||
                  null
                }
                onChange={(e, val) => {
                  handleFormChange("project", val?.id || "");
                }}
                onInputChange={(e, newInputValue) => {
                  setProjectSearch(newInputValue);
                }}
                disabled={!storedDivision?.division_id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Project"
                    error={!!formErrors.project}
                    helperText={formErrors.project}
                  />
                )}
              />
            </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  size="small"
                  fullWidth
                  label="Division"
                  value={storedDivision?.division || "No Division Selected"}
                  disabled
                  InputProps={{
                    sx: {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                  helperText="Division is set globally"
                  error={!storedDivision?.division_id}
                />
              </Grid>

          </Grid>

          {/* Row 2 */}
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Bill No"
                value={formData.bill_no}
                onChange={(e) => handleFormChange("bill_no", e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                size="small"
                label="Bill Date"
                value={formData.bill_date}
                onChange={(e) => handleFormChange("bill_date", e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: new Date().toISOString().split("T")[0],
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                size="small"
                fullWidth
                select
                SelectProps={{ native: true }}
                value={formData.payment_status}
                onChange={(e) =>
                  handleFormChange("payment_status", e.target.value)
                }
              >
                <option value="">Select Payment Status</option>
                <option value="Unpaid">Unpaid</option>
                <option value="Paid">Paid</option>
                <option value="Partial">Partial</option>
              </TextField>
            </Grid>
            {/* Attachment */}
            <Grid item xs={12} md={3}>
              <Box>
                <TextField
                  fullWidth
                  type="file"
                  name="attachment"
                  onChange={(e) => {
                    handleFormChange("attachment", e.target.files[0]);
                    setExistingFileName(""); // Clear existing file name
                  }}
                  label={existingFileName ? "Change Attachment" : "Attachment"}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  error={!!formErrors.attachment}
                />

                {/* ✅ Show existing file info */}
                {existingFileName && (
                  <Box
                    mt={0.5}
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                  >
                    <span>Current: {existingFileName}</span>
                    {formData.attachment &&
                      typeof formData.attachment === "string" && (
                        <IconButton
                          size="small"
                          component="a"
                          href={formData.attachment}
                          target="_blank"
                        >
                          <Visibility
                            sx={{ fontSize: "1rem", color: "#1976d2" }}
                          />
                        </IconButton>
                      )}
                  </Box>
                )}

                {formErrors.attachment && (
                  <Box
                    sx={{ color: "error.main", fontSize: "0.75rem", mt: 0.5 }}
                  >
                    {formErrors.attachment}
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* ✅ Table - Mobile Cards / Desktop Table */}
          <Box mt={3}>
            {isMobile ? (
              // ✅ MOBILE VIEW - Vertical Cards
              <Box>
                {rows.map((row, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 3,
                      p: 2,
                      border: "1px solid #ccc",
                      borderRadius: "8px",
                      backgroundColor: "#fafafa",
                    }}
                  >
                    {/* Header with S.No and Delete */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      <strong>Entry #{index + 1}</strong>
                      <IconButton
                        onClick={() => handleDeleteRow(index)}
                        color="error"
                        size="small"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {/* Expense */}
                    <Autocomplete
                      size="small"
                      options={expenseLists}
                      getOptionLabel={(opt) => opt.expense || ""}
                      value={
                        expenseLists.find(
                          (opt) => opt.expense_id === row.expense,
                        ) || null
                      }
                      onChange={(e, val) =>
                        handleRowChange(index, "expense", val?.expense_id || "")
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Expense" fullWidth />
                      )}
                      sx={{ mb: 2 }}
                    />

                    {/* Cost Type */}
                    <Autocomplete
                      size="small"
                      options={costTypeOptions}
                      getOptionLabel={(opt) => opt.label}
                      value={
                        costTypeOptions.find(
                          (opt) => opt.value === row.cost_type,
                        ) || null
                      }
                      onChange={(e, val) =>
                        handleRowChange(index, "cost_type", val?.value || "")
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Cost Type" fullWidth />
                      )}
                      sx={{ mb: 2 }}
                    />

                    {/* Amount */}
                    <TextField
                      type="number"
                      size="small"
                      label="Amount"
                      fullWidth
                      value={row.amount}
                      onChange={(e) =>
                        handleRowChange(index, "amount", e.target.value)
                      }
                      sx={{ mb: 2 }}
                    />

                    {/* GST */}
                    <TextField
                      type="number"
                      size="small"
                      label="GST"
                      fullWidth
                      value={row.gst}
                      onChange={(e) =>
                        handleRowChange(index, "gst", e.target.value)
                      }
                      sx={{ mb: 2 }}
                    />

                    {/* TDS */}
                    <TextField
                      type="number"
                      size="small"
                      label="TDS"
                      fullWidth
                      value={row.tds}
                      onChange={(e) =>
                        handleRowChange(index, "tds", e.target.value)
                      }
                      sx={{ mb: 2 }}
                    />

                    {/* Total Amount */}
                    <TextField
                      type="number"
                      size="small"
                      label="Total Amount"
                      fullWidth
                      value={row.total_amount}
                      disabled
                      sx={{
                        "& input": { fontWeight: "bold", color: "#000" },
                      }}
                    />
                  </Box>
                ))}

                {/* Add Row Button */}
                <Box textAlign="center" sx={{ mb: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={handleAddRow}
                    startIcon={<AddCircleOutlineIcon />}
                    fullWidth
                  >
                    Add Row
                  </Button>
                </Box>
              </Box>
            ) : (
              // ✅ DESKTOP VIEW - Table
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow
                        sx={{
                          backgroundColor: "#f5f5f5",
                          "& th": {
                            fontWeight: "bold",
                            color: "#333",
                            borderBottom: "1px solid #999",
                          },
                        }}
                      >
                        <TableCell>Sl. No.</TableCell>
                        <TableCell>Expense</TableCell>
                        <TableCell>Cost Type</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>GST</TableCell>
                        <TableCell>TDS</TableCell>
                        <TableCell>Total Amount</TableCell>
                        <TableCell>Delete</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row, index) => (
                        <TableRow key={index}>
                          <TableCell>{index + 1}</TableCell>

                          <TableCell>
                            <Autocomplete
                              size="small"
                              options={expenseLists}
                              getOptionLabel={(opt) => opt.expense || ""}
                              value={
                                expenseLists.find(
                                  (opt) => opt.expense_id === row.expense,
                                ) || null
                              }
                              onChange={(e, val) =>
                                handleRowChange(
                                  index,
                                  "expense",
                                  val?.expense_id || "",
                                )
                              }
                              renderInput={(params) => (
                                <TextField {...params} label="Expense" />
                              )}
                              sx={{ width: 150 }}
                            />
                          </TableCell>

                          <TableCell>
                            <Autocomplete
                              size="small"
                              options={costTypeOptions}
                              getOptionLabel={(opt) => opt.label}
                              value={
                                costTypeOptions.find(
                                  (opt) => opt.value === row.cost_type,
                                ) || null
                              }
                              onChange={(e, val) =>
                                handleRowChange(
                                  index,
                                  "cost_type",
                                  val?.value || "",
                                )
                              }
                              renderInput={(params) => (
                                <TextField {...params} label="Cost Type" />
                              )}
                              sx={{ width: 150 }}
                            />
                          </TableCell>

                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={row.amount}
                              onChange={(e) =>
                                handleRowChange(index, "amount", e.target.value)
                              }
                              sx={{
                                width: 150,
                                "& .MuiInputBase-root": { height: 40 },
                              }}
                            />
                          </TableCell>

                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              placeholder="GST"
                              value={row.gst}
                              onChange={(e) =>
                                handleRowChange(index, "gst", e.target.value)
                              }
                              sx={{
                                width: 120,
                                "& .MuiInputBase-root": { height: 40 },
                              }}
                            />
                          </TableCell>

                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              placeholder="TDS"
                              value={row.tds}
                              onChange={(e) =>
                                handleRowChange(index, "tds", e.target.value)
                              }
                              sx={{
                                width: 120,
                                "& .MuiInputBase-root": { height: 40 },
                              }}
                            />
                          </TableCell>

                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              placeholder="Total"
                              value={row.total_amount}
                              disabled
                              sx={{
                                width: 120,
                                "& .MuiInputBase-root": { height: 40 },
                                "& input": {
                                  fontWeight: "bold",
                                  color: "#000",
                                },
                              }}
                            />
                          </TableCell>

                          <TableCell>
                            <Tooltip title="Delete Row">
                              <IconButton
                                onClick={() => handleDeleteRow(index)}
                                color="error"
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box mt={2} textAlign="center">
                  <Tooltip title="Add Row">
                    <IconButton onClick={handleAddRow} color="primary">
                      <AddCircleOutlineIcon fontSize="large" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </>
            )}

            {/* ✅ Narration & Grand Total - Responsive */}
            <Box
              mt={2}
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                justifyContent: "space-between",
                alignItems: isMobile ? "stretch" : "center",
                gap: 2,
                px: 2,
              }}
            >
              {/* Narration Field */}
              <TextField
                size="small"
                fullWidth
                required
                label="Narration"
                value={formData.narration}
                multiline
                onChange={(e) => handleFormChange("narration", e.target.value)}
                error={!!formErrors.narration}
                helperText={formErrors.narration}
                sx={{ maxWidth: isMobile ? "100%" : "80%" }}
              />

              {/* Grand Total */}
              <Box
                textAlign={isMobile ? "center" : "right"}
                sx={{
                  minWidth: isMobile ? "100%" : "auto",
                  p: isMobile ? 2 : 0,
                  backgroundColor: isMobile ? "#f5f5f5" : "transparent",
                  borderRadius: isMobile ? "8px" : 0,
                }}
              >
                <strong>Grand Total: ₹ {grandTotal.toFixed(2)}</strong>
              </Box>
            </Box>

            {formErrors.expense && (
              <Box mt={1} textAlign="center" color="error.main">
                {formErrors.expense}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default GeneralVoucherForm;
